import { useState, useEffect } from "react";
import { Vendor, DirectorUser, OrderingLanguage, POClassification } from "@po-management/types";
import { useApiClient } from "../auth/useApiClient";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useAppRole } from "../auth/useAppRole";
import { usePO } from "./hooks/usePO";
import { VendorSelector } from "../components/vendors/VendorSelector";
import { CreateVendorForm } from "../components/vendors/CreateVendorForm";
import { ServiceLinesTable, ServiceLineRow } from "./components/ServiceLinesTable";
import { ApproverSelectModal } from "./components/ApproverSelectModal";

interface POFormProps {
  id?: string;
  onDone: () => void;
}

interface FormState {
  vendorContactName: string;
  vendorContactEmail: string;
  orderingLanguage: OrderingLanguage;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  documentDate: string;
  deliveryDate: string;
  paymentTermsDays: string;
  paymentTermsJustification: string;
  agreementRef: string;
  specialTerms: string;
  classification: POClassification;
}

const today = new Date().toISOString().slice(0, 10);

const EMPTY: FormState = {
  vendorContactName: "",
  vendorContactEmail: "",
  orderingLanguage: "English",
  contactName: "",
  contactRole: "",
  contactEmail: "",
  documentDate: today,
  deliveryDate: "",
  paymentTermsDays: "60",
  paymentTermsJustification: "",
  agreementRef: "",
  specialTerms: "",
  classification: "None",
};

function field(
  label: string,
  id: string,
  value: string,
  onChange: (v: string) => void,
  opts?: { type?: string; required?: boolean; error?: string; disabled?: boolean; multiline?: boolean; placeholder?: string }
): JSX.Element {
  const { type = "text", required = false, error, disabled = false, multiline = false, placeholder } = opts ?? {};
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
        {label}{required && " *"}
      </label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          rows={3}
          style={{ width: "100%", padding: "6px 8px", border: `1px solid ${error ? "red" : "#ccc"}`, borderRadius: 4, boxSizing: "border-box", resize: "vertical" }}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          style={{ width: "100%", padding: "7px 8px", border: `1px solid ${error ? "red" : "#ccc"}`, borderRadius: 4, boxSizing: "border-box" }}
        />
      )}
      {error && <div style={{ color: "red", fontSize: 12, marginTop: 2 }}>{error}</div>}
    </div>
  );
}

function sectionTitle(title: string): JSX.Element {
  return (
    <h3 style={{ margin: "24px 0 12px", fontSize: 14, color: "#5B6A8A", borderBottom: "1px solid #eee", paddingBottom: 6 }}>
      {title}
    </h3>
  );
}

export function POForm({ id: poId, onDone }: POFormProps): JSX.Element {
  const { post, put } = useApiClient();
  const currentUser = useCurrentUser();
  const { isOfficeAdmin, isSuperAdmin } = useAppRole();
  const { po: existingPO, loading: loadingPO } = usePO(poId);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [showCreateVendor, setShowCreateVendor] = useState(false);
  const [lines, setLines] = useState<ServiceLineRow[]>([{ itemNo: 1, name: "", qty: "1", unitPrice: "0" }]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | "lines" | "api", string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showApproverModal, setShowApproverModal] = useState(false);

  const set = (key: keyof FormState) => (v: string): void => setForm((f) => ({ ...f, [key]: v }));

  // Pre-fill contact from logged-in user on mount (new PO only)
  useEffect(() => {
    if (currentUser && !poId) {
      setForm((f) => ({ ...f, contactName: currentUser.name, contactEmail: currentUser.email }));
    }
  }, [currentUser, poId]);

  // Pre-fill from existing PO when editing
  useEffect(() => {
    if (!existingPO) return;

    setSelectedVendor({
      id: existingPO.vendorId,
      name: existingPO.vendorNameSnapshot,
      address: existingPO.vendorAddressSnapshot,
      vat: existingPO.vendorVatSnapshot,
      taxOffice: existingPO.vendorTaxOfficeSnapshot,
      defaultPaymentTerms: existingPO.paymentTermsDays,
      createdByOid: "",
      createdAt: new Date(),
      isActive: true,
    });

    setForm({
      vendorContactName: existingPO.vendorContactName,
      vendorContactEmail: existingPO.vendorContactEmail,
      orderingLanguage: existingPO.orderingLanguage,
      contactName: existingPO.contactName,
      contactRole: existingPO.contactRole ?? "",
      contactEmail: existingPO.contactEmail,
      documentDate: existingPO.documentDate ? String(existingPO.documentDate).slice(0, 10) : today,
      deliveryDate: existingPO.deliveryDate ? String(existingPO.deliveryDate).slice(0, 10) : "",
      paymentTermsDays: String(existingPO.paymentTermsDays),
      paymentTermsJustification: existingPO.paymentTermsJustification ?? "",
      agreementRef: existingPO.agreementRef ?? "",
      specialTerms: existingPO.specialTerms ?? "",
      classification: existingPO.classification as POClassification,
    });
    if (existingPO.serviceLines) {
      setLines(existingPO.serviceLines.map((l) => ({
        itemNo: l.itemNo,
        name: l.name,
        qty: String(l.qty),
        unitPrice: String(l.unitPrice),
      })));
    }
  }, [existingPO]);

  function handleVendorChange(v: Vendor | null): void {
    setSelectedVendor(v);
    if (v) setForm((f) => ({ ...f, paymentTermsDays: String(v.defaultPaymentTerms) }));
  }

  function validate(forSubmit = false): boolean {
    const e: typeof errors = {};
    if (!selectedVendor && !poId) e.vendorContactName = "Please select a vendor first";
    if (!form.vendorContactName.trim()) e.vendorContactName = "Required";
    if (!form.vendorContactEmail.trim()) e.vendorContactEmail = "Required";
    if (!form.contactName.trim()) e.contactName = "Required";
    if (!form.contactEmail.trim()) e.contactEmail = "Required";
    if (!form.documentDate) e.documentDate = "Required";
    if (!form.deliveryDate) e.deliveryDate = "Required";
    const pt = parseInt(form.paymentTermsDays, 10);
    if (isNaN(pt) || pt < 1) e.paymentTermsDays = "Must be at least 1 day";
    if (forSubmit && pt < 60 && !form.paymentTermsJustification.trim()) {
      e.paymentTermsJustification = "Justification required when payment terms < 60 days";
    }
    const validLines = lines.filter((l) => l.name.trim() && parseFloat(l.qty) > 0);
    if (validLines.length === 0) e.lines = "At least one service line with a name and quantity > 0 is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function buildPayload(): object {
    return {
      vendorId: selectedVendor?.id ?? existingPO?.vendorId,
      vendorContactName: form.vendorContactName,
      vendorContactEmail: form.vendorContactEmail,
      orderingLanguage: form.orderingLanguage,
      contactName: form.contactName,
      contactRole: form.contactRole || undefined,
      contactEmail: form.contactEmail,
      documentDate: form.documentDate,
      deliveryDate: form.deliveryDate,
      paymentTermsDays: parseInt(form.paymentTermsDays, 10),
      paymentTermsJustification: form.paymentTermsJustification || undefined,
      agreementRef: form.agreementRef || undefined,
      specialTerms: form.specialTerms || undefined,
      classification: form.classification,
      serviceLines: lines
        .filter((l) => l.name.trim())
        .map((l, i) => ({
          itemNo: i + 1,
          name: l.name,
          qty: parseFloat(l.qty) || 0,
          unitPrice: parseFloat(l.unitPrice) || 0,
        })),
    };
  }

  async function handleSaveDraft(): Promise<void> {
    if (!validate(false)) return;
    setSubmitting(true);
    setErrors({});
    try {
      const res = poId
        ? await put(`/api/pos/${poId}`, buildPayload())
        : await post("/api/pos", buildPayload());
      const body = await res.json() as { success: boolean; data?: { id: string }; error?: string };
      if (!res.ok || !body.success) {
        setErrors({ api: body.error ?? "Failed to save PO" });
        setSubmitting(false);
        return;
      }
      onDone();
    } catch {
      setErrors({ api: "Network error — please try again" });
      setSubmitting(false);
    }
  }

  async function handleSubmit(approver: DirectorUser): Promise<void> {
    setShowApproverModal(false);
    setSubmitting(true);
    setErrors({});
    try {
      let currentId = poId;
      if (!currentId) {
        const res = await post("/api/pos", buildPayload());
        const body = await res.json() as { success: boolean; data?: { id: string }; error?: string };
        if (!res.ok || !body.success || !body.data) { setErrors({ api: body.error ?? "Failed to save PO" }); setSubmitting(false); return; }
        currentId = body.data.id;
      } else {
        const res = await put(`/api/pos/${currentId}`, buildPayload());
        if (!res.ok) { setErrors({ api: "Failed to update PO" }); setSubmitting(false); return; }
      }

      const subRes = await post(`/api/pos/${currentId}/submit`, {
        assignedApproverOid: approver.oid,
        assignedApproverName: approver.name,
      });
      const subBody = await subRes.json() as { success: boolean; error?: string };
      if (!subRes.ok || !subBody.success) { setErrors({ api: subBody.error ?? "Failed to submit PO" }); setSubmitting(false); return; }
      onDone();
    } catch {
      setErrors({ api: "Network error — please try again" });
      setSubmitting(false);
    }
  }

  function handleSubmitClick(): void {
    if (!validate(true)) return;
    setShowApproverModal(true);
  }

  if (poId && loadingPO) {
    return <div style={{ padding: "2rem" }}>Loading...</div>;
  }

  const pt = parseInt(form.paymentTermsDays, 10);
  const showJustification = !isNaN(pt) && pt < 60;

  return (
    <div style={{ padding: "2rem", maxWidth: 800, fontFamily: "sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <button
          onClick={onDone}
          style={{ background: "none", border: "none", color: "#5B6A8A", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 0 }}
        >
          ←
        </button>
        <h2 style={{ margin: 0, color: "#5B6A8A" }}>{poId ? `Edit PO${existingPO ? ` — ${existingPO.poNumber}` : ""}` : "New Purchase Order"}</h2>
      </div>

      {errors.api && <div style={{ padding: "10px 14px", background: "#fff0f0", border: "1px solid #fcc", borderRadius: 4, color: "#c00", marginBottom: 16, fontSize: 13 }}>{errors.api}</div>}

      {/* ── Vendor ─────────────────────────────────────────── */}
      {sectionTitle("Vendor")}
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Vendor *</label>
        <VendorSelector
          value={selectedVendor}
          onChange={handleVendorChange}
          onCreateNew={() => setShowCreateVendor(true)}
          disabled={submitting}
        />
        {showCreateVendor && (
          <CreateVendorForm
            onCreated={(v) => { handleVendorChange(v); setShowCreateVendor(false); }}
            onCancel={() => setShowCreateVendor(false)}
          />
        )}
        {selectedVendor && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
            {selectedVendor.address} · VAT: {selectedVendor.vat} · Tax Office: {selectedVendor.taxOffice}
          </div>
        )}
      </div>
      {field("Vendor Contact Name", "vendorContactName", form.vendorContactName, set("vendorContactName"), { required: true, error: errors.vendorContactName })}
      {field("Vendor Contact Email", "vendorContactEmail", form.vendorContactEmail, set("vendorContactEmail"), { type: "email", required: true, error: errors.vendorContactEmail })}

      {/* ── Ordering Language ───────────────────────────────── */}
      {sectionTitle("Ordering Party Language")}
      <div style={{ marginBottom: 14 }}>
        <label htmlFor="orderingLanguage" style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
          Language *
        </label>
        <select
          id="orderingLanguage"
          value={form.orderingLanguage}
          onChange={(e) => set("orderingLanguage")(e.target.value)}
          disabled={submitting}
          style={{ padding: "7px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }}
        >
          <option value="English">English</option>
          <option value="Greek">Greek</option>
        </select>
      </div>

      {/* ── Contact Person ─────────────────────────────────── */}
      {sectionTitle("Contact Person (Threenitas)")}
      {field("Full Name", "contactName", form.contactName, set("contactName"), { required: true, error: errors.contactName })}
      {field("Title / Role", "contactRole", form.contactRole, set("contactRole"), { placeholder: "e.g. Project Manager" })}
      {field("Email", "contactEmail", form.contactEmail, set("contactEmail"), { type: "email", required: true, error: errors.contactEmail, disabled: true })}

      {/* ── Dates & Terms ──────────────────────────────────── */}
      {sectionTitle("Dates & Payment Terms")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>{field("Document Date", "documentDate", form.documentDate, set("documentDate"), { type: "date", required: true, error: errors.documentDate })}</div>
        <div>{field("Delivery Date", "deliveryDate", form.deliveryDate, set("deliveryDate"), { type: "date", required: true, error: errors.deliveryDate })}</div>
      </div>
      {field("Payment Terms (days)", "paymentTermsDays", form.paymentTermsDays, set("paymentTermsDays"), { type: "number", required: true, error: errors.paymentTermsDays })}
      {showJustification && (
        <div style={{ padding: "10px 14px", background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 4, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#7d4e00" }}>⚠ Payment terms below 60 days — justification required</div>
          {field("Justification", "paymentTermsJustification", form.paymentTermsJustification, set("paymentTermsJustification"), {
            required: true,
            multiline: true,
            error: errors.paymentTermsJustification,
            placeholder: "Explain why payment terms are below 60 days...",
          })}
        </div>
      )}
      {field("Agreement Reference", "agreementRef", form.agreementRef, set("agreementRef"), { placeholder: "Optional agreement or engagement letter ref." })}
      {field("Special Terms", "specialTerms", form.specialTerms, set("specialTerms"), { multiline: true, placeholder: "Optional — appears on the PO document" })}

      {/* ── Service Lines ──────────────────────────────────── */}
      {sectionTitle("Service Lines")}
      {errors.lines && <div style={{ color: "red", fontSize: 12, marginBottom: 8 }}>{errors.lines}</div>}
      <ServiceLinesTable lines={lines} onChange={setLines} disabled={submitting} />

      {/* ── Admin Section ──────────────────────────────────── */}
      {(isOfficeAdmin || isSuperAdmin) && (
        <>
          {sectionTitle("Admin Section")}
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="classification" style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Classification</label>
            <select
              id="classification"
              value={form.classification}
              onChange={(e) => set("classification")(e.target.value)}
              disabled={submitting}
              style={{ padding: "7px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }}
            >
              <option value="None">None</option>
              <option value="ESPA">ΕΣΠΑ</option>
              <option value="Symfonhtiko">Συμφωνητικό</option>
            </select>
          </div>
        </>
      )}

      {/* ── Actions ────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 12, marginTop: 24, paddingTop: 16, borderTop: "1px solid #eee" }}>
        <button
          type="button"
          onClick={() => void handleSaveDraft()}
          disabled={submitting}
          style={{ padding: "9px 20px", background: "#fff", border: "1px solid #5B6A8A", color: "#5B6A8A", borderRadius: 4, cursor: submitting ? "default" : "pointer", fontWeight: 500 }}
        >
          {submitting ? "Saving..." : "Save Draft"}
        </button>
        <button
          type="button"
          onClick={handleSubmitClick}
          disabled={submitting}
          style={{ padding: "9px 20px", background: "#5B6A8A", color: "#fff", border: "none", borderRadius: 4, cursor: submitting ? "default" : "pointer", fontWeight: 500 }}
        >
          Submit for Approval
        </button>
        <button
          type="button"
          onClick={onDone}
          disabled={submitting}
          style={{ padding: "9px 16px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: submitting ? "default" : "pointer", color: "#333", fontSize: "inherit" }}
        >
          Cancel
        </button>
      </div>

      {showApproverModal && (
        <ApproverSelectModal
          onConfirm={(approver) => void handleSubmit(approver)}
          onCancel={() => setShowApproverModal(false)}
        />
      )}
    </div>
  );
}
